import { CheckCircle, Schedule } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import useDialog from '../../../../hooks/useDialog';
import useErrors from '../../../../hooks/useErrors';
import { useFetch } from '../../../../hooks/useFecth';
import server from '../../../../services/server';
import SelectClubs from './SelectClubs';

function formatAsaasOption(sub) {
    const id = sub?.id || sub?.subscriptionId;
    const value = Number(sub?.value ?? 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    });
    const status = sub?.status || '—';
    const cycle = sub?.cycle || '';
    const customer = sub?.customer?.name || sub?.customerName || '';
    return [id, value, status, cycle, customer].filter(Boolean).join(' · ');
}

export function Migration({ mutate, club, contract }) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const { id: from } = useDialog('#migration');
    const navigation = useNavigate();

    const clubRef = club ?? contract?.Club;
    const clubId = clubRef?.id ?? contract?.clubId;
    const planId = contract?.planId ?? contract?.plan?.id ?? contract?.contractData?.plan?.id;

    const {
        data: asaasData,
        isFetching: loadingAsaas,
        error: asaasError,
    } = useFetch(contract?.uuid ? `subscription/${contract.uuid}/asaas-subscriptions` : null);

    const asaasSubscriptions = asaasData?.subscriptions || [];

    const { values, isSubmitting, setFieldValue, resetForm, handleSubmit } = useFormik({
        initialValues: {
            id: contract?.Club?.originId || '',
            subscriptionAsaasId: contract?.subscriptionAsaasId || '',
            action: '',
        },
        onSubmit: async (formValues, { setSubmitting }) => {
            try {
                setSubmitting(true);

                if (formValues.action === 'migrate-club') {
                    if (!planId) {
                        throw { message: 'Defina o plano da proposta antes de migrar.', response: { status: 400 } };
                    }

                    const { data } = await server.post(`migration/club`, {
                        id: formValues.id,
                        planId: Number(planId),
                        clubId,
                        contract,
                        proposalUuid: contract?.uuid,
                        subscriptionAsaasId: formValues.subscriptionAsaasId || undefined,
                    });

                    enqueueSnackbar(data.message);
                    await mutate();
                    resetForm();
                    if (['advertiser', 'club'].includes(from)) {
                        navigation(`/dashboard/club/${data.data.id}`, { replace: true });
                    }
                }

            } catch (error) {
                resetForm({
                    values: {
                        id: formValues.id,
                        subscriptionAsaasId: formValues.subscriptionAsaasId,
                        action: '',
                    },
                });
                setErrors(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleMigrateClub = () => {
        setFieldValue('action', 'migrate-club');
        handleSubmit();
    };

    return (
        <Paper elevation={2} sx={{ p: 3 }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Migração de Clube
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Escolha o clube legado e, se existir, a assinatura Asaas já paga. Ao migrar, o
                        clube nasce com as regras desta proposta. Os assinantes são migrados depois, na
                        aba Migração do clube.
                    </Typography>
                </Box>

                <Stack spacing={2}>
                    <SelectClubs
                        value={contract?.Club?.originId || values.id || null}
                        disabled={Boolean(contract?.clubId)}
                        name="id"
                        club={clubRef || null}
                        onChange={(name, value) => setFieldValue(name, value)}
                    />

                    <FormControl fullWidth size="small">
                        <InputLabel id="asaas-sub-label">Assinatura Asaas</InputLabel>
                        <Select
                            labelId="asaas-sub-label"
                            label="Assinatura Asaas"
                            value={values.subscriptionAsaasId || ''}
                            disabled={isSubmitting || loadingAsaas}
                            onChange={e => setFieldValue('subscriptionAsaasId', e.target.value)}
                            endAdornment={
                                loadingAsaas ? (
                                    <CircularProgress size={18} sx={{ mr: 2 }} />
                                ) : null
                            }
                        >
                            <MenuItem value="">
                                <em>Nenhuma / selecionar depois</em>
                            </MenuItem>
                            {asaasSubscriptions.map(sub => {
                                const id = sub?.id || sub?.subscriptionId;
                                if (!id) return null;
                                return (
                                    <MenuItem key={id} value={id}>
                                        {formatAsaasOption(sub)}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>

                    {asaasError && (
                        <Alert severity="warning">
                            Não foi possível listar assinaturas Asaas. Preencha o documento no termo e
                            tente de novo.
                        </Alert>
                    )}

                    {!loadingAsaas && !asaasError && asaasData?.warning && (
                        <Alert severity="info">{asaasData.warning}</Alert>
                    )}

                    {!loadingAsaas && !asaasError && !asaasData?.warning && asaasSubscriptions.length === 0 && (
                        <Alert severity="info">
                            Nenhuma assinatura Asaas encontrada para o documento desta proposta.
                        </Alert>
                    )}

                    {contract?.subscriptionAsaasId && (
                        <Alert severity="success">
                            Assinatura vinculada: {contract.subscriptionAsaasId}
                        </Alert>
                    )}
                </Stack>

                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                    {Boolean(contract?.Club?.originId) ? (
                        <Button
                            variant="outlined"
                            onClick={() =>
                                navigation(`/dashboard/clubes/${contract.clubId}?tab=migracao`)
                            }
                        >
                            Ir para migração de assinantes
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={isSubmitting || !values.id || !planId}
                            onClick={handleMigrateClub}
                            startIcon={isSubmitting ? <Schedule /> : <CheckCircle />}
                        >
                            {isSubmitting ? 'Migrando...' : 'Migrar'}
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Paper>
    );
}
